import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { purgeConversationCache } from "@/lib/local-store";
import type { AppNotification, PublicProfile } from "@/types";

export function useNotifications() {
  return useQuery({
    queryKey: ["notifications"],
    queryFn: () => api.get<AppNotification[]>("/api/notifications")
  });
}

export function useMarkNotificationRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post(`/api/notifications/${id}/read`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] })
  });
}

export function useMarkAllNotificationsRead() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => api.post("/api/notifications/read-all"),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["notifications"] })
  });
}

export function useBlockedUsers() {
  return useQuery({
    queryKey: ["blocked"],
    queryFn: () => api.get<PublicProfile[]>("/api/moderation/blocked")
  });
}

export function useBlockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.post<{ ok: boolean; wipedConversationId: string | null }>("/api/moderation/block", { userId }),
    onSuccess: async (result) => {
      if (result.wipedConversationId) {
        await purgeConversationCache(result.wipedConversationId);
        queryClient.removeQueries({ queryKey: ["messages", result.wipedConversationId] });
        queryClient.removeQueries({ queryKey: ["conversation", result.wipedConversationId] });
      }
      queryClient.invalidateQueries({ queryKey: ["blocked"] });
      queryClient.invalidateQueries({ queryKey: ["friends"] });
      queryClient.invalidateQueries({ queryKey: ["conversations"] });
    }
  });
}

export function useUnblockUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.delete(`/api/moderation/block/${userId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["blocked"] })
  });
}

export function useReportContent() {
  return useMutation({
    mutationFn: (input: {
      targetType: "user" | "message" | "post";
      targetId: string;
      reason: string;
      /** For E2EE messages: the plaintext the reporter's own client already decrypted and displayed. */
      evidencePlaintext?: string;
    }) => api.post("/api/moderation/report", input)
  });
}
